import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  formatDateMain,
  formatNumberWithCommas,
  businessDisplayName,
  planUnit,
  type SubscriptionInterval,
} from '@hero/core';
import {
  getSubscriptionCreditsRemaining,
  isUsageSubscription,
  type Language,
  type SubscriptionPublic,
} from '@hero/schema';
import { cn } from '@hero/web-lib';
import { CalendarIcon, PackageIcon, SparklesIcon, TruckIcon } from '@hero/web-icons';
import { Button } from '../../../ui/Button/Button';
import { SubscriptionStatusBadge } from '../SubscriptionStatusBadge';

export interface CurrentPlanCardProps {
  /** Caller's subscription, or `null` for the implicit free tier. */
  subscription: SubscriptionPublic | null;
  /** Live fleet size (vehicles). Used to display overage vs committed. */
  actualFleetTotal?: number;
  /** Loading state shown while the subscription is being fetched. */
  isLoading?: boolean;

  /**
   * Primary "change plan" CTA — historically opened the unified
   * `BillingCheckoutScreen` so the user could bump tier / change
   * interval / change seats from one button.
   *
   * **Currently NOT rendered when a Subscription exists.** Per the
   * "paid plan = Subscription doc exists; free is modeled as no
   * Subscription" invariant ([sales/README.md → Free tier](../../../../../../../apps/backend/src/app/sales/subscription/README.md#free-tier)),
   * an org reaching the populated branch of this card already has a
   * paid plan. The catch-all "Upgrade plan" button was confusing in
   * that state — it dropped users into a full plan grid when what
   * they actually needed was just "add more trucks" without touching
   * tier or interval. The dedicated add-trucks (and tier-change)
   * flows are coming in their own feature; until then the populated
   * card shows no self-serve repricing affordance.
   *
   * The prop is preserved on the API so wiring in
   * `BillingOverviewPage` doesn't churn when the new flow lands. The
   * no-Subscription branch keeps using `onSubscribe`, which is what
   * actually fires the "first-time paid plan" flow.
   */
  onChangePlan?: () => void;
  /**
   * Whether the org has an unsettled Billing (`pending` /
   * `partially_paid` / `past_due`).
   *
   * Held on the API for the future add-trucks / change-plan flow,
   * which will need to suppress its CTA whenever a previous
   * purchase still needs to be paid (the page-level
   * `EnterpriseUpgradeCard` already gates on the same signal).
   * Not consumed inside this card today.
   */
  hasPendingBilling?: boolean;
  /**
   * Cancel subscription handler. Intentionally accepted but NOT
   * rendered yet — the cancel flow needs proration / "ends at period
   * end" handling we haven't wired up (see TODO in the action row).
   * Keep the prop on the API so the page wiring stays stable for when
   * we re-introduce the button.
   */
  onCancel?: () => void;
  /** CTA shown when there is no subscription (free tier). */
  onSubscribe?: () => void;
}

/**
 * Headline card for the BillingOverviewPage and admin Org → Subscription tab.
 *
 * Stateless — every action is a callback. The manager hook owns navigation
 * and mutations; this card only renders the current sub state, including
 * the inline trial banner (folded in here so the overview stops showing
 * the trial date twice).
 */
export function CurrentPlanCard({
  subscription,
  actualFleetTotal,
  isLoading,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- intentionally
  // accepted but not rendered while paid orgs have no self-serve
  // repricing surface here. See JSDoc on `onChangePlan` + the
  // TODO(billing-add-trucks) action-row comment below.
  onChangePlan: _onChangePlan,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- held on
  // the API for the future add-trucks flow; not consumed today (see
  // JSDoc on `hasPendingBilling`).
  hasPendingBilling: _hasPendingBilling,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- intentionally
  // accepted but not rendered yet; cancel flow needs proration handling
  // before we surface the action. See action-row TODO.
  onCancel: _onCancel,
  onSubscribe,
}: CurrentPlanCardProps) {
  const { t, i18n } = useTranslation('m-billing');

  if (isLoading) {
    return (
      <ShellCard>
        <div className="h-32 w-full animate-pulse rounded-lg bg-gray-100" />
      </ShellCard>
    );
  }

  if (!subscription) {
    return (
      <ShellCard>
        <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <PlanGlyph />
            <div className="flex flex-col gap-1">
              <h3 className="text-lg font-semibold text-gray-900">
                {t('subscription.no_subscription_title')}
              </h3>
              <p className="text-sm text-gray-500">
                {t('subscription.no_subscription_description')}
              </p>
            </div>
          </div>
          {onSubscribe && (
            <Button onClick={onSubscribe} className="shrink-0">
              {t('subscription.actions.subscribe')}
            </Button>
          )}
        </div>
      </ShellCard>
    );
  }

  const planCode = subscription.purchase.planCode;
  const planTitle = t(`plan.${planCode}.title`, {
    defaultValue: planCode,
  });
  const productLabel = subscription.purchase.business
    ? businessDisplayName(subscription.purchase.business, i18n.language as Language)
    : undefined;
  // A prepaid subscription has no cycle to name, so the subtitle drops
  // to just the currency rather than inventing "Monthly" for something
  // that is bought once and spent whenever.
  const isPrepaid = isUsageSubscription(subscription);
  const creditsRemaining = getSubscriptionCreditsRemaining(subscription);
  const intervalLabel = subscription.interval
    ? resolveIntervalLabel(t, subscription.interval)
    : undefined;
  const unit = subscription.purchase.unit ?? planUnit(planCode);

  const overage =
    !isPrepaid && unit !== 'order' && actualFleetTotal !== undefined
      ? Math.max(0, actualFleetTotal - subscription.committedVehicleQty)
      : undefined;
  const committedDisplay =
    overage !== undefined && overage > 0
      ? `${formatNumberWithCommas(subscription.committedVehicleQty)} (+${formatNumberWithCommas(overage)})`
      : formatNumberWithCommas(subscription.committedVehicleQty);

  return (
    <ShellCard>
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <PlanGlyph />
          <div className="flex flex-col gap-0.5">
            {/* Which brand this contract belongs to. Tier names
                are shared across products ("Start", "Pro"), so the title
                alone does not say what the account is subscribed to.
                Absent on subscriptions predating the field. */}
            {productLabel ? (
              <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                {productLabel}
              </span>
            ) : null}
            <h3 className="text-lg font-semibold text-gray-900">{planTitle}</h3>
            <p className="text-sm text-gray-500">
              {intervalLabel ? `${intervalLabel} · ` : ''}
              {subscription.currency}
            </p>
          </div>
        </div>
        <SubscriptionStatusBadge status={subscription.status} />
      </div>

      {/* Trial inline note — folded in so the overview no longer
          shows the trial banner + this card both displaying the same
          end date. */}
      {subscription.status === 'trialing' && subscription.trialEndsAt && (
        <TrialInlineNote
          trialEndsAt={subscription.trialEndsAt}
          label={t('subscription.fields.trial_ends')}
        />
      )}

      {/* Field grid */}
      <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {isPrepaid ? (
          // What is left and what has been used — the two numbers a
          // prepaid customer opens this page for. Neither a seat count
          // nor a renewal date exists to put here.
          <>
            {/* The balance is allowed to go below zero: free orders are
                spent against it up front, so an account that used its
                allowance owes that many before the next purchase counts.
                Rendering that as "−1 remaining" reads as a bug, so a
                negative balance switches label and says what it is. */}
            {creditsRemaining < 0 ? (
              <FieldRow
                icon={<PackageIcon className="size-4" />}
                label={t('subscription.fields.credits_owed')}
                value={formatNumberWithCommas(Math.abs(creditsRemaining))}
              />
            ) : (
              <FieldRow
                icon={<PackageIcon className="size-4" />}
                label={t('subscription.fields.credits_remaining')}
                value={formatNumberWithCommas(creditsRemaining)}
              />
            )}
            <FieldRow
              icon={<PackageIcon className="size-4" />}
              label={t('subscription.fields.credits_used')}
              value={`${formatNumberWithCommas(
                subscription.balance?.consumed ?? 0,
              )} / ${formatNumberWithCommas(subscription.balance?.purchased ?? 0)}`}
            />
          </>
        ) : (
          <>
            <FieldRow
              icon={<TruckIcon className="size-4" />}
              label={t('subscription.fields.committed_vehicles')}
              value={committedDisplay}
            />
            <FieldRow
              icon={<CalendarIcon className="size-4" />}
              label={t('subscription.fields.next_renewal')}
              value={formatDateOrDash(subscription.currentPeriodEnd)}
            />
          </>
        )}
      </dl>

      {/*
        Action row.

        Intentionally empty for orgs with an active Subscription.

        TODO(billing-add-trucks): Re-introduce a self-serve action
        here when the dedicated "Add more trucks" repricing flow
        lands. That flow will:
          - Live on its own route (NOT the full plan grid).
          - Only need `committedVehicleQty` + proration confirmation
            — no plan / interval picker.
          - Use the existing `updateOpen` + `repriceOrRemintCheckout`
            pipeline already documented in
            `apps/backend/src/app/sales/README.md`.
        Until then we deliberately do NOT surface the catch-all
        "Upgrade plan" button when a Subscription exists — see
        the "Self-serve upgrade visibility" section of that README
        for the rationale.

        TODO(billing-cancel-flow): Re-introduce a `<Button>` here that
        calls `onCancel` once we wire up the cancel-at-period-end flow
        end-to-end (proration, paid-period grace, banner copy). The
        `onCancel` prop is intentionally kept on the API so the page
        wiring doesn't have to change when we put the button back.
      */}
    </ShellCard>
  );
}

/** Card shell matching the checkout-screen visual language. */
function ShellCard({ children }: { children: React.ReactNode }) {
  return (
    <section
      className={cn(
        'flex flex-col gap-5 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm',
      )}>
      {children}
    </section>
  );
}

/** Primary-tinted plan glyph reused across the empty + populated states. */
function PlanGlyph() {
  return (
    <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
      <SparklesIcon className="size-5" />
    </div>
  );
}

interface FieldRowProps {
  icon?: React.ReactNode;
  label: string;
  value: React.ReactNode;
}

function FieldRow({ icon, label, value }: FieldRowProps) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-gray-100 bg-gray-50/50 px-3 py-2.5">
      <dt className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-gray-500">
        {icon && <span className="text-gray-400">{icon}</span>}
        {label}
      </dt>
      <dd className="text-sm font-semibold tabular-nums text-gray-900">{value}</dd>
    </div>
  );
}

function TrialInlineNote({
  trialEndsAt,
  label,
}: {
  trialEndsAt: Date | string;
  label: string;
}) {
  return (
    <div className="flex items-center gap-2 rounded-xl border border-primary/20 bg-primary/[0.04] px-3 py-2 text-sm text-primary">
      <CalendarIcon className="size-4 shrink-0" />
      <span className="font-medium">
        {label}: {formatDateOrDash(trialEndsAt)}
      </span>
    </div>
  );
}

/**
 * Resolve an interval to a human label.
 *
 * The `m-billing.json` `subscription.interval_label.<interval>` map is
 * the primary source. Legacy subscription docs may still carry retired
 * values (notably `'week'` from before 2026-06) — in that case
 * `react-i18next` returns the key itself, which would leak `interval_label.week`
 * into the UI. We detect that, fall back to a safe friendly label, and
 * fail open with the raw interval string for anything we don't know.
 */
function resolveIntervalLabel(
  t: (key: string) => string,
  interval: SubscriptionInterval | string,
): string {
  const key = `subscription.interval_label.${interval}`;
  const translated = t(key);
  if (translated && translated !== key) return translated;
  return FALLBACK_INTERVAL_LABELS[interval] ?? String(interval);
}

const FALLBACK_INTERVAL_LABELS: Record<string, string> = {
  month: 'Monthly',
  semester: 'Every 6 months',
  year: 'Yearly',
};

function formatDateOrDash(d: Date | string | undefined): string {
  if (!d) return '—';
  const date = typeof d === 'string' ? new Date(d) : d;
  if (Number.isNaN(date.getTime())) return '—';
  return formatDateMain({ date, format: 'date' });
}
